<template>
  <div
    ref="mobilePanelPopup"
    class="mobile-panel-popup">
    <div
      class="mobile-panel-popup__head"
      :class="{
        'mobile-panel-popup__head--touch': userDevice.hasTouch,
        'mobile-panel-popup__head--empty-title': !title,
      }">
      <div
        v-if="!userDevice.hasTouch && isClosable"
        class="mobile-panel-popup__close shape-cross"
        @click="dismissPopup" />
      <div
        v-if="userDevice.hasTouch && isClosable"
        class="mobile-panel-popup__bar-wrap"
        @touchstart.prevent="setDragging">
        <div class="mobile-panel-popup__bar" />
      </div>
      <div
        v-if="preTitle"
        class="mobile-panel-popup__title">
        {{ preTitle }}
      </div>
      <div class="mobile-panel-popup__title-wrap">
        <div
          v-if="title"
          class="mobile-panel-popup__title">
          {{ title }}
        </div>
        <div
          v-if="isPaypal"
          class="mobile-panel-popup__logo">
          <div class="mobile-panel-popup__logo-text">
            Check out with
          </div>
          <img
            src="/images/paypal.svg"
            alt=""
            class="mobile-panel-popup__logo-img">
        </div>
      </div>
    </div>
    <div class="mobile-panel-popup__body">
      <div class="mobile-panel-popup__content">
        <component
          :is="component"
          :data="data" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { supportsPassiveEvent, throttle } from 'Root/helpers/tools';

export default {
  name: 'MobilePanelPopup',

  props: {
    title: {
      type: String,
      default: '',
    },

    preTitle: {
      type: String,
      default: '',
    },

    component: {
      type: Object,
      default: () => ({}),
    },

    data: {
      type: Object,
      default: () => ({}),
    },
    name: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      touchPos: { x: 0, y: 0 },
      isDragging: false,
      translateY: 0,
      threshold: 0.2,
      popupHeight: 0,
    };
  },

  computed: {
    ...mapState(['userDevice']),

    throttledWindowResizeHandler() {
      return throttle(() => {
        this.setPopupHeight();
      }, 100);
    },

    isClosable() {
      return this.data.isClosable !== undefined ? this.data.isClosable : true;
    },
    isPaypal() {
      return this.name === 'paypal-popup';
    },
  },

  mounted() {
    // window.addEventListener('touchstart', this.touchStart);
    window.addEventListener('touchmove', this.touchMove, supportsPassiveEvent ? { passive: true } : false);
    window.addEventListener('touchend', this.unsetDragging);
    this.setPopupHeight();
    window.addEventListener('resize', this.throttledWindowResizeHandler);
  },

  beforeDestroy() {
    // window.removeEventListener('touchstart', this.touchStart);
    window.removeEventListener('touchmove', this.touchMove);
    window.removeEventListener('touchend', this.unsetDragging);
    window.removeEventListener('resize', this.throttledWindowResizeHandler);
  },

  methods: {
    closePopup(options) {
      this.$vuedals.close({
        ...options,
        name: 'mobile-panel-popup',
      });
    },

    dismissPopup() {
      if (!this.isClosable) {
        return;
      }

      this.$vuedals.dismiss({ name: 'mobile-panel-popup' });
    },

    touchStart(event) {
      this.touchPos.y = event.touches[0].pageY;
    },

    touchMove(event) {
      if (!this.isDragging) {
        return;
      }

      const deltaY = event.touches[0].pageY - this.touchPos.y;
      this.touchPos.y = event.touches[0].pageY;

      this.translateY += deltaY;
      if (this.translateY <= 0) {
        return;
      }

      this.$refs.mobilePanelPopup.style.transform = `translateY(${this.translateY}px)`;
      if (this.translateY > this.popupHeight * this.threshold) {
        this.dismissPopup();
      }
    },

    setDragging(event) {
      if (!this.isClosable) {
        return;
      }

      this.touchStart(event);
      this.isDragging = true;
    },

    unsetDragging() {
      this.isDragging = false;
    },

    setPopupHeight() {
      this.popupHeight = this.$refs.mobilePanelPopup.offsetHeight;
    },

  },
};
</script>

<style lang="scss">
  .vuedal.mobile-panel-popup {
    transition: all .4s ease;
    width: initial;
  }

  @media screen and #{$screen-less-than-640} {
    .vuedals {
      z-index: 2147483010 !important; // поверх интеркома
    }

    .vuedal.mobile-panel-popup {
      left: 0;
      transform: translate(0, 0);
    }

    .vuedals.vuedal-enter-active,
    .vuedals.vuedal-enter-to {
      overflow-y: hidden;
    }

    .vuedals.vuedal-enter-active .mobile-panel-popup {
      transform: translate(0, 10%);
    }

    .vuedals.vuedal-enter-to .mobile-panel-popup {
      transform: translate(0, 0);
    }

    .vuedal.mobile-panel-popup {
      margin: 0;
      bottom: 0;
      width: 100% !important;
      max-height: 90vh !important;
      overflow: hidden;
    }
  }
</style>

<style lang="scss" scoped>
  .mobile-panel-popup {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow-y: hidden;
    position: relative;
    background-color: $color--basic-black;
    border-radius: 15px;
  }

  .mobile-panel-popup__close {
    --shape-color: #{$color--basic-superdark-gray};

    width: 16px;
    height: 16px;
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 1;
  }

  .mobile-panel-popup__body {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    overflow-y: auto;
    padding: 0 75px 45px;
  }

  .mobile-panel-popup__head {
    padding: 20px 31px 0; // 45px пространство для крестика
  }

  .mobile-panel-popup__head--touch {
    padding: 15px 20px 0;
  }

  .mobile-panel-popup__head--empty-title {
    margin-bottom: 30px;
  }

  .mobile-panel-popup__title {
    text-align: center;
    font-size: 17px;
    font-weight: 500;
    margin: 30px 25px 30px 0
  }

  .mobile-panel-popup__title-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .mobile-panel-popup__logo {
    width: 120px;
    padding: 6px 10px;
    border-radius: 65px;
    background-color: $color--basic-white;
    display: flex;
    gap: 6px;
  }

  .mobile-panel-popup__logo-text {
    font-size: 7px;
    line-height: 8px;
    text-align: center;
    color: #002991;
    font-family: 'Rubik', sans-serif;
    font-weight: 500;
  }

  .mobile-panel-popup__logo-img {
    width: 54px;
  }

  .mobile-panel-popup__content {
    width: 100%;
    display: flex;
    flex-direction: column;
    //overflow-y: hidden;
  }

  @media screen and #{$screen-less-than-640} {
    .mobile-panel-popup {
      border-radius: 45px 45px 0 0;
    }

    .mobile-panel-popup__body {
      flex-wrap: wrap;
      padding: 0 16px 16px;
    }

    .mobile-panel-popup__head--empty-title {
      margin-bottom: 20px;
    }

    .mobile-panel-popup__title {
      font-size: 17px;
    }

    .mobile-panel-popup__accent {
      text-align: center;
      font-size: 17px;
    }

    .mobile-panel-popup__bar-wrap {
      height: 100%;
      margin-top: -18px;
      padding-top: 18px;
      position: relative;

      &:before {
        content: "";
        display: block;
        width: 100%;
        height: 50px;
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    .mobile-panel-popup__bar {
      width: 60px;
      height: 6px;
      background: $color--basic-dark-gray;
      opacity: .45;
      border-radius: 59px;
      margin: 0 auto;
    }

    .mobile-panel-popup__close {
      top: 16px;
      right: 24px;
    }

    .mobile-panel-popup.upsale-instruction-popup {
      position: absolute;
      bottom: 0;
    }
  }
</style>
